import { useEffect, useState } from 'react';
import { onAuthStateChanged, type User } from 'firebase/auth';
import { doc, onSnapshot, type Unsubscribe } from 'firebase/firestore';
import { CLAIM_KEYS } from '@repx/shared';
import { auth, db } from '../lib/firebase';

export interface ResolvedClaims {
  role: 'admin' | 'project_coordinator' | 'client' | null;
  clientId: string | null;
  assignedClientIds: string[];
}

export interface AuthState {
  loading: boolean;
  user: User | null;
  claims: ResolvedClaims;
  refresh: () => Promise<void>;
}

const EMPTY: ResolvedClaims = { role: null, clientId: null, assignedClientIds: [] };

function parse(raw: Record<string, unknown> | undefined): ResolvedClaims {
  if (!raw) return EMPTY;
  const role = raw[CLAIM_KEYS.role];
  if (role !== 'admin' && role !== 'project_coordinator' && role !== 'client') return EMPTY;
  return {
    role,
    clientId: (raw[CLAIM_KEYS.clientId] as string | undefined) ?? null,
    assignedClientIds: (raw[CLAIM_KEYS.assignedClientIds] as string[] | undefined) ?? [],
  };
}

// Admins and PCs (staff) can preview any client they can read via rules.
// Clients are always scoped to their own clientId.
export function effectiveClientId(
  claims: ResolvedClaims,
  previewParam: string | null,
): { clientId: string | null; previewAs: string | null } {
  if (claims.role === 'client') return { clientId: claims.clientId, previewAs: null };
  if ((claims.role === 'admin' || claims.role === 'project_coordinator') && previewParam) {
    return { clientId: previewParam, previewAs: previewParam };
  }
  return { clientId: null, previewAs: null };
}

export function isStaff(c: ResolvedClaims): boolean {
  return c.role === 'admin' || c.role === 'project_coordinator';
}

export function useAuthClaims(): AuthState {
  const [user, setUser] = useState<User | null>(auth.currentUser);
  const [claims, setClaims] = useState<ResolvedClaims>(EMPTY);
  const [loading, setLoading] = useState(true);

  async function read(u: User, force = false): Promise<ResolvedClaims> {
    const token = await u.getIdTokenResult(force);
    return parse(token.claims as Record<string, unknown>);
  }

  async function refresh() {
    if (!auth.currentUser) return;
    setClaims(await read(auth.currentUser, true));
  }

  useEffect(() => {
    let userDocUnsub: Unsubscribe | null = null;
    let lastSeen: number | null = null;

    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      userDocUnsub?.();
      userDocUnsub = null;
      lastSeen = null;
      if (!u) {
        setClaims(EMPTY);
        setLoading(false);
        return;
      }
      setClaims(await read(u, false));
      setLoading(false);

      userDocUnsub = onSnapshot(doc(db, `users/${u.uid}`), async (snap) => {
        const ts = snap.get('claimsUpdatedAt') as { toMillis?: () => number } | null | undefined;
        const mark = ts?.toMillis?.() ?? null;
        if (mark == null) return;
        if (lastSeen !== null && mark !== lastSeen) {
          setClaims(await read(u, true));
        }
        lastSeen = mark;
      });
    });

    return () => {
      unsub();
      userDocUnsub?.();
    };
  }, []);

  return { loading, user, claims, refresh };
}
